import { CircleCheck } from '@/assets/icons/circle-check';
import { Icon, Icons } from '@/components/icon';
import { Combobox, ComboboxGroup, ComboboxItem } from '@/components/ui/combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { useActivityFilterOptions } from './use-activity-filter-options';

interface ActivityValueInputProps {
  condition: string;
  value: string;
  onChange: (value: string) => void;
  autoOpen?: boolean;
}

export function ActivityValueInput({
  condition,
  value,
  onChange,
  autoOpen,
}: ActivityValueInputProps) {
  const { t } = useTranslation('desktop_ui');
  const { teamMemberOptions, activityTypeOptions, isLoading } = useActivityFilterOptions();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
    }
  }, [autoOpen]);

  if (condition === 'from') {
    const selectedMember = teamMemberOptions.find((opt) => opt.value === value);
    const searchPlaceholder = t('components.companies.filter.activity.searchTeamMember');
    const selectMemberText = t('components.companies.filter.activity.selectTeamMember');
    const loadingText = t('components.contacts.table.loading');

    // Determine display text: if value exists but no match found, show loading or keep showing value
    const displayText = value
      ? selectedMember?.label || (isLoading ? loadingText : selectMemberText)
      : selectMemberText;

    return (
      <Combobox
        variant="filter"
        searchPlaceholder={searchPlaceholder}
        options={teamMemberOptions}
        value={value}
        onValueChange={onChange}
        open={open}
        onOpenChange={setOpen}
        trigger={
          <button
            type="button"
            className="h-full min-w-16 max-w-32 border-r border-primary-10 bg-transparent px-2 outline-none cursor-pointer hover:bg-primary-5 data-[state=open]:bg-primary-5 text-left truncate"
          >
            <P3 className={cn('truncate', value ? 'text-primary-100' : 'text-primary-60')}>
              {displayText}
            </P3>
          </button>
        }
      >
        {({ filteredOptions }) => (
          <ComboboxGroup variant="filter">
            {isLoading ? (
              <div className="flex items-center justify-center py-2">
                <P3 className="text-primary-60">{t('components.contacts.table.loading')}</P3>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="flex items-center justify-center py-2">
                <P3 className="text-primary-60">
                  {t('components.companies.filter.activity.noTeamMembers')}
                </P3>
              </div>
            ) : (
              filteredOptions.map((option) => (
                <ComboboxItem key={option.value} option={option} variant="filter">
                  {(opt, isSelected) => (
                    <div className="flex w-full items-center justify-between gap-1">
                      <div className="flex items-center gap-1">
                        <Icon name={Icons.User} className="text-s text-primary-80" />
                        <P3 className="text-primary-100">{opt.label}</P3>
                      </div>
                      {isSelected && <CircleCheck className="size-4 text-highlight-default" />}
                    </div>
                  )}
                </ComboboxItem>
              ))
            )}
          </ComboboxGroup>
        )}
      </Combobox>
    );
  }

  if (condition === 'is') {
    const selectedType = activityTypeOptions.find((opt) => opt.value === value);
    const selectTypeText = t('components.companies.filter.activity.selectType');

    // Display the label if found, otherwise show placeholder
    const displayText = value ? selectedType?.label || value : selectTypeText;

    return (
      <Select value={value} onValueChange={onChange} open={open} onOpenChange={setOpen}>
        <SelectTrigger
          showChevron={false}
          className="h-full text-s min-h-0 border-0 border-r border-primary-10 rounded-none shadow-none px-2 outline-none cursor-pointer hover:bg-primary-5 data-[state=open]:bg-primary-5 focus:ring-0 [&_[data-slot=select-value]]:gap-0 [&_[data-slot=select-value]]:text-s [&_[data-slot=select-value]]:text-primary-100 [&_[data-slot=select-value]]:font-medium"
        >
          <SelectValue placeholder={selectTypeText}>
            <P3 className={value ? 'text-primary-100' : 'text-primary-60'}>{displayText}</P3>
          </SelectValue>
        </SelectTrigger>
        <SelectContent
          showChevron={false}
          className="w-[200px] min-w-0 rounded-2xl border border-primary-5 bg-white p-2 shadow-md [&_[data-slot=select-viewport]]:space-y-1"
        >
          {activityTypeOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="h-6 cursor-pointer rounded-md px-1 py-0 pr-8 text-s font-medium text-primary-100 outline-none data-[highlighted]:bg-primary-3"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return null;
}
